import {i18n} from '@yourcoach/i18n';

import {authStore} from '../../api/auth';
import {currentUserStore} from '../../api/user';
import MegaphoneIcon from '../../assets/icons/megaphone.svg';
import {alert} from '../../components/alert';
import {msi} from '../../styles/normalizeSize';
import {Clickable} from '../../uikit/Clickable';
import {Text} from '../../uikit/Text';
import {logger} from '../../utils/logger';
import {platform} from '../../utils/platform';
import {PRIVACY_POLICY_URL, TOS_URL} from '../../utils/resources';
import {TestID} from '../../utils/test-utils/data-test-id';

type Config = {
  openDocument: ({
    title,
    url,
  }: {
    showAlert: () => void;
    title: string;
    url: string;
  }) => any;
};

let inProgress = false;

export const privacyPolicyUpdateService = (config: Config) =>
  new Promise<void>(async resolve => {
    if (inProgress) {
      resolve();

      return;
    }

    const innerResolve = () => {
      inProgress = false;

      resolve();
    };

    if (!currentUserStore.user) {
      innerResolve();

      return;
    }

    try {
      inProgress = true;

      const version = await currentUserStore.getTosVersion();

      if (version > currentUserStore.user.tos_version) {
        showAlert(config, innerResolve);

        return;
      }

      innerResolve();
    } catch (error) {
      logger.error(error);

      innerResolve();
    }
  });

const showAlert = (
  config: Config,
  resolve: (value: PromiseLike<void> | void) => void,
) => {
  const message = i18n
    .t('notifications:privacy_policy_update.description')
    .split(/<%(.*?)%>/)
    .filter(Boolean)
    .map((part, i) => {
      const key = `${part}-${i}`;

      if (part.includes('pp->') || part.includes('tos->')) {
        const title = part.includes('pp->')
          ? i18n.t('shared:privacy_policy')
          : i18n.t('shared:tos');

        const url = part.includes('pp->') ? PRIVACY_POLICY_URL : TOS_URL;
        const text = part.replace('pp->', '').replace('tos->', '');

        return (
          <Clickable
            key={key}
            onClick={() =>
              config.openDocument({
                showAlert: () => showAlert(config, resolve),
                title,
                url,
              })
            }
            style={
              platform.isWeb
                ? {
                    display: 'inline-block',
                  }
                : undefined
            }
          >
            <Text
              color={'global2'}
              style={
                platform.isNative
                  ? {
                      position: 'relative',
                      top: msi(2.5),
                    }
                  : undefined
              }
              variant="subtitle"
              weight="semibold"
            >
              {text}
            </Text>
          </Clickable>
        );
      }

      return part;
    });

  alert.show({
    buttons: [
      {
        label: i18n.t('notifications:privacy_policy_update.agree_button'),
        onClick: () => {
          currentUserStore.acceptTos().catch(() => {});

          resolve();
        },
        scheme: 'secondary',
        testID: TestID.privacy_policy_update.button.agree,
      },
      {
        label: i18n.t('notifications:privacy_policy_update.disagree_button'),
        onClick: () => {
          setTimeout(() => {
            currentUserStore.clear();
            authStore.logout().catch(() => {});
            authStore._deleteSession();

            resolve();
          });
        },
        testID: TestID.privacy_policy_update.button.decline,
      },
    ],
    icon: MegaphoneIcon,
    iconColor: 'global2',
    renderMessage: () => (
      <Text
        style={{
          textAlign: 'center',
        }}
        variant="subtitle"
      >
        {message}
      </Text>
    ),
    testID: TestID.privacy_policy_update.block.alert,
    title: i18n.t('notifications:privacy_policy_update.title'),
    variant: 'promo',
  });
};
